import React, {
    PropsWithChildren,
    ReactNode,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
} from 'react'

import { IdPAdapter } from '../adapters/IdPAdapter'
import { StorageAdapter } from '../adapters/StorageAdapter'
import { Session } from '../machine/types'
import { AuthLogger, AuthRuntime } from '../runtime/AuthRuntime'
import { AuthContext } from './AuthContext'

export interface AuthConfig {
    adapter: IdPAdapter
    storage?: StorageAdapter
    storageKey?: string
    logger?: AuthLogger
    refreshBufferMs?: number
    refreshCooldownMs?: number
    now?: () => number
    onBeforeLogout?: (session: Session) => Promise<void>
}

export interface AuthProviderProps extends AuthConfig {
    /** Optional fallback rendered while init() is in flight (state.tag === 'initializing'). */
    fallback?: ReactNode
    /** Skip rendering children until init() resolves. Default: true. */
    waitForInit?: boolean
}

/**
 * Constructs the runtime once and exposes it via singleton context.
 *
 * The runtime is a stateful object; we hold it in `useState`'s lazy
 * initializer so it's created exactly once and survives re-renders.
 * Adapter / storage swaps require a full remount — that's intentional.
 */
export const AuthProvider: React.FC<PropsWithChildren<AuthProviderProps>> = ({
    adapter,
    storage,
    storageKey,
    logger,
    refreshBufferMs,
    refreshCooldownMs,
    now,
    onBeforeLogout,
    fallback = null,
    waitForInit = true,
    children,
}) => {
    const [runtime] = useState(
        () =>
            new AuthRuntime({
                adapter,
                storage,
                storageKey,
                logger,
                refreshBufferMs,
                refreshCooldownMs,
                now,
                onBeforeLogout,
            }),
    )

    const [booted, setBooted] = useState(false)
    useEffect(() => {
        let cancelled = false
        void runtime.init().finally(() => {
            if (!cancelled) {
                setBooted(true)
            }
        })
        return () => {
            cancelled = true
        }
    }, [runtime])

    const value = useMemo(() => ({ runtime }), [runtime])

    if (waitForInit && !booted) {
        return <>{fallback}</>
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Internal helper: subscribe to the runtime's state via React 18's
 * `useSyncExternalStore`. Exported for the package-internal hooks; not
 * part of the public surface.
 */
export const useAuthRuntime = (): AuthRuntime => {
    const ctx = React.useContext(AuthContext)
    if (!ctx) {
        throw new Error(
            'aos-auth: hook used outside of <AuthProvider>. Wrap your tree with <AuthProvider adapter={...} />.',
        )
    }
    return ctx.runtime
}

export const useAuthState = () => {
    const runtime = useAuthRuntime()
    return useSyncExternalStore(
        listener => runtime.subscribe(listener),
        () => runtime.getState(),
        () => runtime.getState(),
    )
}
